import { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { FiGrid, FiPackage, FiShoppingBag, FiUsers, FiTag, FiGift, FiLogOut, FiMenu, FiX, FiExternalLink, FiChevronRight, FiImage, FiClipboard, FiSettings } from 'react-icons/fi';
import styles from './AdminLayout.module.css';

const NAV = [
  { to:'/admin',            label:'Dashboard',  icon:FiGrid,        roles:['moderator','admin'] },
  { to:'/admin/products',   label:'Products',   icon:FiPackage,     roles:['editor','moderator','admin'] },
  { to:'/admin/categories', label:'Categories', icon:FiTag,         roles:['editor','moderator','admin'] },
  { to:'/admin/banners',    label:'Banners',    icon:FiImage,       roles:['moderator','admin'] },
  { to:'/admin/orders',     label:'Orders',     icon:FiShoppingBag, roles:['moderator','admin'] },
  { to:'/admin/drafts',     label:'Drafts',     icon:FiClipboard,   roles:['moderator','admin'] },
  { to:'/admin/coupons',    label:'Coupons',    icon:FiGift,        roles:['moderator','admin'] },
  { to:'/admin/users',      label:'Users',      icon:FiUsers,       roles:['admin'] },
  { to:'/admin/settings',   label:'Settings',   icon:FiSettings,    roles:['admin'] },
];

const ROLE_META = {
  admin:     { label:'Administrator', bg:'#dbeafe33', text:'#60a5fa', emoji:'👑' },
  moderator: { label:'Moderator',     bg:'#d1fae533', text:'#34d399', emoji:'🛡️' },
  editor:    { label:'Editor',        bg:'#fef3c733', text:'#fbbf24', emoji:'✏️' },
  user:      { label:'User',          bg:'#f3f4f633', text:'#9ca3af', emoji:'👤' },
};

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const visibleNav = NAV.filter(n => n.roles.includes(user?.role || 'user'));
  const rm = ROLE_META[user?.role] || ROLE_META.user;
  const current = NAV.find(n => n.to === '/admin' ? location.pathname === '/admin' : location.pathname.startsWith(n.to));

  return (
    <div className={styles.layout}>
      {/* Sidebar */}
      <aside className={`${styles.sidebar} ${open ? styles.sidebarOpen : ''}`}>
        <div className={styles.sTop}>
          <Link to="/admin" className={styles.sLogo} onClick={() => setOpen(false)}>⚙️ BD<strong>Admin</strong></Link>
          <button className={styles.sClose} onClick={() => setOpen(false)} aria-label="Close"><FiX size={18}/></button>
        </div>

        <div className={styles.rolePill} style={{ background:rm.bg }}>
          <span>{rm.emoji}</span>
          <span style={{ color:rm.text, fontWeight:700, fontSize:'.76rem' }}>{rm.label}</span>
        </div>

        <nav className={styles.sNav}>
          {visibleNav.map(item => {
            const Icon = item.icon;
            const active = item.to === '/admin' ? location.pathname === '/admin' : location.pathname.startsWith(item.to);
            return (
              <Link key={item.to} to={item.to} className={`${styles.navItem} ${active ? styles.navActive : ''}`} onClick={() => setOpen(false)}>
                <Icon size={17}/> <span>{item.label}</span>
                {active && <FiChevronRight size={13} style={{ marginLeft:'auto', opacity:.5 }}/>}
              </Link>
            );
          })}
        </nav>

        <div className={styles.sFooter}>
          <div className={styles.adminInfo}>
            <div className={styles.adminAv}>{user?.name?.[0]?.toUpperCase()}</div>
            <div className={styles.adminMeta}>
              <p>{user?.name}</p>
              <p>{user?.email}</p>
            </div>
          </div>
          <div className={styles.footBtns}>
            <Link to="/" className={styles.footBtn} target="_blank"><FiExternalLink size={12}/> View Store</Link>
            <button className={`${styles.footBtn} ${styles.footLogout}`} onClick={() => { logout(); navigate('/'); }}>
              <FiLogOut size={12}/> Logout
            </button>
          </div>
        </div>
      </aside>

      {open && <div className={styles.overlay} onClick={() => setOpen(false)} aria-hidden="true"/>}

      {/* Main */}
      <div className={styles.main}>
        <header className={styles.topbar}>
          <button className={styles.menuBtn} onClick={() => setOpen(true)} aria-label="Menu"><FiMenu size={22}/></button>
          <h1 className={styles.pageTitle}>{current?.label || 'Admin Panel'}</h1>
          <div className={styles.topRight}>
            <Link to="/" className="btn btn-outline btn-sm hide-mobile" target="_blank"><FiExternalLink size={12}/> Store</Link>
            <div className={styles.topAv} title={user?.name}>{user?.name?.[0]?.toUpperCase()}</div>
          </div>
        </header>
        <main className={styles.content}><Outlet/></main>
      </div>
    </div>
  );
}
